import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import { Lead } from "@mapmycustomers/shared/types/entity";
import { CategorizedMapEntries, EntityPin } from "@mapmycustomers/shared/types/map";
import User from "@mapmycustomers/shared/types/User";
import { ModifiedMapFilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/MapFilterModel";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";
import { convertLatLngBoundsToPlatformBounds } from "@mapmycustomers/shared/util/geo/GeoService";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import { UserLocation } from "@app/component/map/FeaturedMap";
import useMap from "@app/component/map/FeaturedMap/utils/useMap";
import RecordStylingContext from "@app/component/RecordCard/utils/RecordStylingContext";
import IMapViewContext from "@app/component/view/types/IMapViewContext";
import BaseMapViewContext from "@app/component/view/utils/BaseMapViewContext";
import AlertBadgesType from "@app/enum/map/AlertBadgesType";
import MarkerEffect from "@app/enum/MarkerEffect";
import EntityAnnotation from "@app/scene/map/components/EntityAnnotation";
import ClusterFeature from "@app/scene/map/components/features/ClusterFeature";
import EntityFeature from "@app/scene/map/components/features/EntityFeature";
import LeadFeature from "@app/scene/map/components/features/LeadFeature";
import MultiPinFeature from "@app/scene/map/components/features/MultiPinFeature";
import { HeatMapLayer, TerritoriesLayer, TrafficLayer } from "@app/scene/map/components/layers";
import Legends from "@app/scene/map/components/Legends";
import CreateEditPinLegendDrawer from "@app/scene/map/components/pinLegends/CreateEditPinLegendDrawer";
import ShowOtherPins from "@app/scene/map/components/ShowOtherPins";
import Toolbar from "@app/scene/map/components/Toolbar";
import LassoAnnotation from "@app/scene/map/components/tools/LassoAnnotation";
import LassoTool from "@app/scene/map/components/tools/LassoTool";
import MapToolInformationPanel from "@app/scene/map/components/tools/MapToolInformationPanel";
import ToolSelectionPanel from "@app/scene/map/components/ToolSelectionPanel";
import Legend from "@app/scene/map/enums/Legend";
import {
  fetchPins,
  initializeLeadFinderMode,
  setLeadAnnotation,
  setOtherPins,
} from "@app/scene/map/store/actions";
import { showAnnotation } from "@app/scene/map/store/mapMode/actions";
import {
  getFilteredLeadFinderRecords,
  getLayerLegendsState,
  getLeadFinderOtherPins,
  getMapEntries,
  getMapLassoPathsCount,
  getMapViewState,
  getVisibleBadges,
} from "@app/scene/map/store/selectors";
import mergeFilterModels from "@app/scene/map/utils/mergeFilterModels";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useMapEventHandler from "@app/util/hook/map/useMapEventHandler";
import { mapPinIdGetter } from "@app/util/map/idGetters";
import defaultEntityStyleGetter from "@app/util/map/markerStyles/defaultEntityStyleGetter";

import ProcessingModal from "../LeadFinderPopover/components/ProcessingModal";

import CenterPointCircle from "./components/CenterPointCircle";
import DuplicatesModal from "./components/DuplicatesModal";
import LeadAnnotation from "./components/LeadAnnotation";
import MoreResults from "./components/MoreResults";
import Sidebar from "./components/Sidebar";
import TopPanel from "./components/TopPanel";
import styles from "./LeadFinderPage.module.scss";

const availableTools: MapTool[] = [MapTool.LASSO, MapTool.TAKE_SNAPSHOT];

// wait for max 500ms before triggering next fetchPins request
const boundsChangeOptions = { maxWait: 500 };

interface Props {
  initialize: typeof initializeLeadFinderMode.request;
  lassoPathsCount: number;
  leadsEntries: Lead[];
  mapEntries: CategorizedMapEntries;
  mapViewState: MapViewState;
  onSetLeadAnnotation: typeof setLeadAnnotation;
  onShowAnnotation: typeof showAnnotation.request;
  otherPins: boolean;
  pinsFetcher: typeof fetchPins.request;
  setOtherPins: typeof setOtherPins;
  visibleBadges: AlertBadgesType[];
}

const LeadFinderPage: React.FC<Props> = ({
  initialize,
  lassoPathsCount,
  leadsEntries,
  mapEntries,
  mapViewState,
  onSetLeadAnnotation,
  onShowAnnotation,
  otherPins,
  pinsFetcher,
  setOtherPins,
  visibleBadges,
}) => {
  const analytics = useExtendedAnalytics("Lead Finder mode", useAnalytics());
  const { map } = useMap();
  const initializationCompleted = useRef(false);

  useEffect(() => {
    // initialization
    const bounds = map.getBounds();
    const zoom = map.getZoom();

    if (bounds && zoom) {
      initialize({
        callback: (bounds) => {
          if (bounds) {
            if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
              map.setCenter(bounds.getCenter());
            } else {
              map.fitBounds(bounds);
            }
          }

          initializationCompleted.current = true;
        },
      });
    }
    analytics.initiated();
  }, [analytics, initialize, map]);

  useMapEventHandler(
    map,
    // We don't use `idle` because it is not triggered if you stopped moving but keep mouse
    // button pressed. Hence, we use debounce to avoid calling backend too frequently.
    // However, we still use maxWait to guarantee that at least one request is sent every
    // maxWait milliseconds. This is needed to load data if user is panning continuously.
    "bounds_changed",
    useDebouncedCallback([
      function (this: google.maps.Map) {
        const bounds = this.getBounds();
        const zoom = this.getZoom();
        if (bounds && zoom && otherPins) {
          pinsFetcher({
            request: {},
            viewport: { bounds: convertLatLngBoundsToPlatformBounds(bounds), zoom },
          });
        }
      },
      100,
      boundsChangeOptions,
    ])
  );

  const handleUpdateFilterModel: IMapViewContext["updateFilterModel"] = useDynamicCallback(
    (newFilterModel: ModifiedMapFilterModel) => {
      const resultingFilterModel = mergeFilterModels(mapViewState.filter, newFilterModel);
      pinsFetcher({ request: { filter: resultingFilterModel } });
    }
  );

  const handleUpdateViewState: IMapViewContext["updateViewState"] = useDynamicCallback(
    (viewState: Partial<MapViewState>) => {
      pinsFetcher({ request: viewState, updateOnly: true });
    }
  );

  const handleUpdateViewAs: IMapViewContext["updateViewAs"] = useDynamicCallback(
    (userId: undefined | User["id"]) => {
      pinsFetcher({ request: { viewAs: userId } });
    }
  );

  const context: IMapViewContext = useMemo(
    () => ({
      updateFilterModel: handleUpdateFilterModel,
      updateViewAs: handleUpdateViewAs,
      updateViewState: handleUpdateViewState,
      updateViewStateAndFetch: pinsFetcher,
      viewState: mapViewState,
    }),
    [handleUpdateFilterModel, handleUpdateViewAs, handleUpdateViewState, mapViewState, pinsFetcher]
  );

  const handleSetOtherPins = useCallback(
    (value) => setOtherPins({ otherPins: value }),
    [setOtherPins]
  );

  const handlePinClick = useCallback(
    (pin) => onShowAnnotation(mapPinIdGetter(pin)),
    [onShowAnnotation]
  );
  const handleLeadClick = useCallback(
    (lead: Lead) => onSetLeadAnnotation(lead.place_id),
    [onSetLeadAnnotation]
  );

  const recordStylingValue = useMemo(
    () => ({
      getPinStyle: defaultEntityStyleGetter({
        areBadgesVisible: visibleBadges.length > 0,
        effect: MarkerEffect.NONE,
        markerSize: MarkerSize.LARGE,
        shape: (entityPin: EntityPin) => entityPin.data.shape ?? MarkerShape.SQUARE,
        showEntityIcon: true,
        visibleBadges,
      }),
    }),
    [visibleBadges]
  );

  return (
    <AnalyticsContext.Provider value={analytics}>
      <RecordStylingContext.Provider value={recordStylingValue}>
        <BaseMapViewContext.Provider value={context}>
          <div className={styles.container}>
            <ToolSelectionPanel availableTools={availableTools} />
            <Toolbar />
            <TopPanel />
            <MapToolInformationPanel showLasso={!lassoPathsCount} />
            <Legends
              allowedLegends={[
                Legend.COLOR_KEY,
                Legend.SHAPE_KEY,
                Legend.BOUNDARIES,
                Legend.ALERTS,
                Legend.HEAT_MAP,
                Legend.TRAFFIC,
              ]}
              className={styles.legends}
            />
            {initializationCompleted.current && <MoreResults />}
          </div>
          {otherPins && (
            <>
              <ClusterFeature clusters={mapEntries.clusters} />
              <MultiPinFeature multiPins={mapEntries.multiPins} />
              <EntityFeature entityPins={mapEntries.entities} onClick={handlePinClick} />
              <EntityAnnotation />
            </>
          )}
          <LeadFeature leads={leadsEntries} onClick={handleLeadClick} />
          <LeadAnnotation leads={leadsEntries} />
          <CenterPointCircle />
          <HeatMapLayer />
          <TerritoriesLayer />
          <TrafficLayer />
          <LassoTool pathLimit={1} />
          <LassoAnnotation leads={leadsEntries} />
          <ShowOtherPins checked={otherPins} onChange={handleSetOtherPins} />
          <Sidebar />
          <UserLocation />
          <ProcessingModal />
          <DuplicatesModal />
          <CreateEditPinLegendDrawer />
        </BaseMapViewContext.Provider>
      </RecordStylingContext.Provider>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => {
  const areBadgesVisible = getLayerLegendsState(state)[Legend.ALERTS]?.visible;
  const visibleBadges = getVisibleBadges(state);

  return {
    lassoPathsCount: getMapLassoPathsCount(state),
    leadsEntries: getFilteredLeadFinderRecords(state),
    mapEntries: getMapEntries(state),
    mapViewState: getMapViewState(state),
    otherPins: getLeadFinderOtherPins(state),
    visibleBadges: areBadgesVisible ? visibleBadges : [],
  };
};

const mapDispatchToProps = {
  initialize: initializeLeadFinderMode.request,
  onSetLeadAnnotation: setLeadAnnotation,
  onShowAnnotation: showAnnotation.request,
  pinsFetcher: fetchPins.request,
  setOtherPins,
};

export default connect(mapStateToProps, mapDispatchToProps)(LeadFinderPage);
